import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import PageTitle from '../../components/PageTitle';
import { byCloudGetRechargeData } from '../../api';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const formatJson = (value) => {
  if (value == null) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

function pickField(obj, ...keys) {
  if (!obj || typeof obj !== 'object') return '';
  for (const k of keys) {
    if (obj[k] != null && String(obj[k]).trim() !== '') return String(obj[k]);
  }
  return '';
}

function statusTag(label, value) {
  const v = String(value || '').trim();
  if (!v) return <Tag>-</Tag>;
  let color = 'default';
  if (v === '2') color = 'success';
  else if (v === '3' || v === '4') color = 'error';
  else if (v === '1') color = 'processing';
  return (
    <Tag color={color}>
      {label}={v}
    </Tag>
  );
}

function errMsg(e) {
  return (
    e?.response?.data?.error ||
    e?.response?.data?.vendor_msg ||
    e?.response?.data?.msg ||
    e?.message ||
    String(e)
  );
}

/**
 * BY 厂家充值单查询（getRechargeData）
 * 完整返回展示在下方 JSON；摘要区便于快速看 task_status / recharge_status。
 * 厂家约 5 秒限流，请勿连续狂点。
 */
export default function ByRechargeOrderQuery() {
  const [businessNumber, setBusinessNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const vendorData = useMemo(() => {
    if (!result) return null;
    const d = result.data;
    if (d && typeof d === 'object' && !Array.isArray(d)) return d;
    const raw = result.raw;
    if (raw && typeof raw === 'object' && raw.data && typeof raw.data === 'object') {
      return raw.data;
    }
    return null;
  }, [result]);

  const summary = useMemo(() => {
    const src = vendorData || {};
    return {
      businessNumber: pickField(src, 'businessnumber', 'businessNumber', 'business_number'),
      taskStatus: pickField(src, 'task_status', 'taskStatus'),
      rechargeStatus: pickField(src, 'recharge_status', 'rechargeStatus'),
      meterNo: pickField(src, 'meter_no', 'meterNo', 'devicenumber', 'deviceNumber'),
    };
  }, [vendorData]);

  const runQuery = async () => {
    const bn = String(businessNumber || '').trim();
    if (!bn) {
      message.warning('请填写厂家充值业务单号 business_number');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const body = await byCloudGetRechargeData(bn);
      setResult(body || null);
      if (body && body.success === false) {
        message.warning(body.vendor_msg || body.error || '厂家返回非成功，请看下方完整 JSON');
      } else {
        message.success('查询完成');
      }
    } catch (e) {
      const msg = errMsg(e);
      setError(msg);
      const body = e?.response?.data;
      if (body && typeof body === 'object') {
        setResult(body);
      }
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <PageTitle title="BY订单查询" />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="查询厂家 getRechargeData（充值业务单状态）"
        description={
          <div>
            <div>输入平台订单上的 by_business_number（厂家业务单号）。下方展示接口完整返回（含 raw）。</div>
            <div>参考：recharge_status=2 有效；task_status=2 充值成功（需两者都为 2）；task_status=4 作废。</div>
            <div>厂家查询约 5 秒限流，请间隔几秒再查，勿连续狂点。</div>
          </div>
        }
      />

      <Card size="small" title="查询条件" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text type="secondary">厂家充值业务单号 business_number</Text>
            <Input
              value={businessNumber}
              onChange={(e) => setBusinessNumber(e.target.value)}
              placeholder="如 20240910…"
              allowClear
              disabled={loading}
              onPressEnter={runQuery}
            />
          </div>
          <Space>
            <Button type="primary" loading={loading} onClick={runQuery}>
              查询
            </Button>
            <Button
              disabled={loading}
              onClick={() => {
                setBusinessNumber('');
                setResult(null);
                setError('');
              }}
            >
              清空
            </Button>
          </Space>
        </Space>
      </Card>

      {error ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} />
      ) : null}

      {result ? (
        <>
          <Card size="small" title="摘要（从返回中抽取）" style={{ marginBottom: 16 }}>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="业务单号">
                {summary.businessNumber || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="表号/设备">
                {summary.meterNo || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Space wrap>
                  {statusTag('task_status', summary.taskStatus)}
                  {statusTag('recharge_status', summary.rechargeStatus)}
                  {result.success === true ? (
                    <Tag color="blue">proxy success</Tag>
                  ) : (
                    <Tag color="orange">proxy 非成功</Tag>
                  )}
                  {result.vendor_code != null ? (
                    <Tag>vendor_code={String(result.vendor_code)}</Tag>
                  ) : null}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="厂家消息">
                {result.vendor_msg || result.error || '-'}
              </Descriptions.Item>
            </Descriptions>
            <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
              完整字段以下方 JSON 为准；摘要仅方便扫一眼。
            </Paragraph>
          </Card>

          <Card size="small" title="完整返回（全部）">
            <TextArea
              value={formatJson(result)}
              readOnly
              autoSize={{ minRows: 16, maxRows: 40 }}
              style={{ fontFamily: 'Consolas, Monaco, monospace', fontSize: 12 }}
            />
          </Card>
        </>
      ) : null}
    </div>
  );
}
